// PromotionsPage — Gold offer, discount codes, referral bonus
// Mirrors the marketing "Gold Offer" page and the 25% discount code banner

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow, TfRewardCard } from '../../components/TfPrimitives';

interface Promo {
  id: string;
  code: string;
  title: string;
  description: string;
  discount: string;
  applies: string;
  expires: string;
  status: 'active' | 'expired' | 'used' | 'available';
  type: 'gold' | 'code' | 'referral' | 'seasonal';
  icon: string;
  accent: string;
}

const PROMOS: Promo[] = [
  { id: 'p1', code: 'FUND25',      title: '25% off first challenge',     description: 'Get 25% off your first challenge purchase. Valid on all tiers.', discount: '25% OFF',     applies: 'First purchase',     expires: '2026-08-15', status: 'available', type: 'code',     icon: '🎟️', accent: '#007AFF' },
  { id: 'p2', code: 'FUNDGOLD50',  title: 'Gold offer · 50% off second', description: 'Buy your first challenge, get 50% off your second one.',     discount: '50% OFF 2nd', applies: 'Bundle deal',         expires: '2026-08-01', status: 'available', type: 'gold',     icon: '🏆', accent: '#FF9500' },
  { id: 'p3', code: 'REFER3',      title: 'Refer 3 friends · 1 free mo.',  description: 'Get 1 month free for every 3 successful referrals.',         discount: 'FREE MONTH',  applies: 'Recurring monthly',  expires: 'Always on', status: 'available', type: 'referral', icon: '👥', accent: '#34C759' },
  { id: 'p4', code: 'SUMMER40',    title: 'Summer 40% off reset fees',   description: 'Summer sale — 40% off all challenge resets.',               discount: '40% OFF',     applies: 'Resets only',        expires: '2026-09-01', status: 'available', type: 'seasonal', icon: '☀️', accent: '#FF3B30' },
  { id: 'p5', code: 'TRIAL15',     title: '15% off any tier',            description: 'Try us — 15% off any challenge tier.',                     discount: '15% OFF',     applies: 'All tiers',         expires: '2026-07-20', status: 'expired',   type: 'code',     icon: '🎟️', accent: '#8E8E93' },
  { id: 'p6', code: 'WELCOME10',   title: 'Welcome · 10% off',            description: 'Already used on your first purchase.',                     discount: '10% OFF',     applies: 'Used',              expires: 'Used',       status: 'used',      type: 'code',     icon: '✓',        accent: '#8E8E93' },
];

export function PromotionsPage() {
  const nav = useNavigate();
  const [claimed, setClaimed] = useState<string[]>([]);
  const [filter, setFilter] = useState<'all' | 'available' | 'used' | 'expired'>('available');
  const [referralLink] = useState('funderpro.live/r/d3m0');

  const filtered = PROMOS.filter((p) => filter === 'all' || p.status === filter);
  const availableCount = PROMOS.filter(p => p.status === 'available').length;
  const usedCount = PROMOS.filter(p => p.status === 'used').length;
  const expiredCount = PROMOS.filter(p => p.status === 'expired').length;

  function copy(text: string, id: string) {
    if (navigator.clipboard) navigator.clipboard.writeText(text);
    if (id) setClaimed((c) => [...c, id]);
    setTimeout(() => setClaimed((c) => c.filter((x) => x !== id)), 2000);
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Promotions</div>
        <div className="w-9" />
      </div>

      {/* Hero — white floating card with massive savings + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>⭐ Gold offer</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>50%</span>
            <span className="text-[15px] text-tf-text-3">off second</span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="text-tf-text-3">Buy any tier, get 50% off a second one. Stack with other codes.</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => copy('FUNDGOLD50', 'gold')} data-testid="hero-copy-gold" className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              {claimed.includes('gold') ? '✓ Copied' : 'Copy code'}
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => nav('/portal/challenge')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Shop now
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-tf-surface-3 px-3 py-1.5 text-[12px] font-mono font-bold">
            <span className="text-[10px] uppercase text-tf-text-3">Code</span>
            <span className="text-tf-text-1">FUNDGOLD50</span>
          </div>
        </TfCardFloating>
      </div>

      {/* 3-up stat row */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'Available', value: `${availableCount}`, color: '#34C759' },
          { label: 'Used', value: `${usedCount}`, color: '#FF9500' },
          { label: 'Expired', value: `${expiredCount}`, color: '#8E8E93' },
        ]}
      />

      {/* Reward card for the referral program — using TfRewardCard */}
      <TfRewardCard
        title="👥 Refer 3 friends, get 1 month free"
        description="For every 3 friends who pass their challenge, you get 1 month free."
        countdown="Always on"
        ctaLabel="Copy"
        onCta={() => copy('https://' + referralLink, 'ref')}
        testId="copy-referral"
      />

      <div className="mx-4 mb-3 flex items-center gap-2 px-1">
        <div className="flex-1 rounded-[10px] border border-tf-border bg-white px-3 py-2 font-mono text-[12px] text-tf-text-1">{referralLink}</div>
        <button onClick={() => copy('https://' + referralLink, 'ref')} data-testid="copy-referral-btn" className="px-4 h-9 rounded-full bg-tf-text-1 text-white text-[13px] font-semibold active:scale-[0.97]">Copy</button>
      </div>

      <div className="mx-4 mb-3 flex items-center gap-3 text-[11px] text-tf-text-3 px-1">
        <span>👤 0 referrals</span>
        <span>•</span>
        <span>🎁 0/3 for next free month</span>
      </div>

      {/* Filter tabs */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {(['available', 'used', 'expired', 'all'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`promo-filter-${f}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === f ? 'bg-tf-text-1 text-white' : 'bg-white border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >{f}</button>
        ))}
      </div>

      {/* Promo cards — wrapped in TfCard for consistent card list look */}
      <TfSectionHeaderView title="All promotions" />
      <div className="mx-4 space-y-2.5">
        {filtered.map((p) => (
          <div
            key={p.id}
            data-testid={`promo-${p.id}`}
            className={['rounded-[16px] bg-white p-4 relative overflow-hidden', p.status === 'available' ? 'border-2 border-tf-orange' : 'border border-tf-border'].join(' ')}
            style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] text-[24px]" style={{ backgroundColor: p.accent + '15' }}>{p.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-[15px] font-bold text-tf-text-1">{p.title}</h3>
                  {p.status === 'expired' && <span className="rounded-full bg-tf-text-3/15 px-1.5 py-0.5 text-[8px] font-bold uppercase text-tf-text-3">Expired</span>}
                  {p.status === 'used' && <span className="rounded-full bg-tf-green-tint px-1.5 py-0.5 text-[8px] font-bold uppercase text-tf-profit">Used</span>}
                </div>
                <p className="text-[11px] text-tf-text-3 mt-0.5">{p.description}</p>
                <div className="mt-1 text-[10px] text-tf-text-3">{p.applies} · Expires {p.expires}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 rounded-[10px] border border-dashed border-tf-border bg-tf-surface-1 px-3 py-2 flex items-center justify-between">
                <span className="font-mono font-bold text-[14px] text-tf-text-1">{p.code}</span>
                <span className="rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" style={{ backgroundColor: p.accent }}>{p.discount}</span>
              </div>
              {p.status === 'available' && (
                <button onClick={() => copy(p.code, p.id)} data-testid={`claim-${p.id}`} className="px-4 h-9 rounded-full bg-tf-text-1 text-white text-[12px] font-semibold active:scale-[0.97]">
                  {claimed.includes(p.id) ? '✓ Copied' : 'Copy'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
